import type { CarWashDataSource } from '@/lib/carwash/data/data-source'

import { STORAGE_KEY } from '@/constants/carwash'
import { migrateState } from '@/lib/carwash/data/migrate'

export class StorageFullError extends Error {
    constructor() {
        super('This device has run out of space for saved data.')
        this.name = 'StorageFullError'
    }
}

const isQuotaError = (error: unknown): boolean => error instanceof DOMException && (
    error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED' || error.code === 22
)

export const createLocalStorageSource = (key = STORAGE_KEY): CarWashDataSource => ({
    load: async () => {
        const raw = window.localStorage.getItem(key)
        if (!raw) return null
        return migrateState(JSON.parse(raw))
    },
    commit: async (_command, next) => {
        try {
            window.localStorage.setItem(key, JSON.stringify(next))
        }
        catch (error) {
            if (isQuotaError(error)) throw new StorageFullError()
            throw error
        }
    },
    clear: async () => {
        window.localStorage.removeItem(key)
    },
    watch: (listener) => {
        const onStorage = (event: StorageEvent) => {
            if (event.key !== key) return

            try {
                listener(event.newValue ? migrateState(JSON.parse(event.newValue)) : null)
            }
            catch {
                // Another tab wrote something unreadable; keep the current state.
            }
        }

        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }
})
